import React from 'react';
import { Image, Text, View } from '@react-pdf/renderer';
import { FallbackImage } from './SVGs';
import styles from './styles';
import type Comparables from '../models/Comparables';

interface RowProps {
  comp: Comparables;
  isStriped: boolean;
}

function randomString() {
  return Math.random().toString(36).slice(2);
}

// the comp might have its own "no photo" image, we want to catch these and display our fallback image
const NO_PHOTO_URLS = ['https://filenet.hotpads.com/images/errors/NoPhotos_500x500.png'];

const Row: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<RowProps>>> = ({
  comp,
  isStriped,
}) => {
  const {
    bubblePrice,
    pricePerSqft,
    street,
    unit,
    propertyPhotos,
    beds,
    baths,
    sqft,
    distance,
    status,
    pets,
    parking,
    laundry,
    heating,
    isActive,
  } = comp;
  let propertyPhoto = propertyPhotos && propertyPhotos[0] && propertyPhotos[0].medium;
  const hasPhoto = propertyPhoto ? !NO_PHOTO_URLS.includes(propertyPhoto) : false;
  propertyPhoto += '?' + randomString(); // workaround to prevent chrome from using incorrect cache entries (RG-906)
  return (
    <View style={isStriped ? styles.tableRow : styles.tableRowStriped} wrap={false}>
      <View style={styles.imageColumn}>{hasPhoto ? <Image src={propertyPhoto as string} /> : <FallbackImage />}</View>
      <View style={styles.priceColumn}>
        <Text style={styles.bodyHeading}>{bubblePrice}/mo</Text>
        <Text style={styles.body}>{pricePerSqft ? `${pricePerSqft} per sqft` : '--'}</Text>
      </View>
      <View style={styles.locationColumn}>
        <Text style={styles.body}>
          {street} {unit && unit !== ' ' ? ', Unit ' + unit : ''}
        </Text>
        <Text style={styles.body}>{distance} miles away</Text>
        <View style={styles.flexRow}>
          <View style={[styles.statusDot, { backgroundColor: isActive ? '#985DFF' : '#A7A6AB' }]} />
          <Text style={styles.body}>{status}</Text>
        </View>
      </View>
      <View style={styles.bedsColumn}>
        <Text style={styles.body}>{beds} beds</Text>
      </View>
      <View style={styles.bathsColumn}>
        <Text style={styles.body}>{baths} baths</Text>
      </View>
      <View style={styles.sizeColumn}>
        <Text style={styles.body}>{sqft ? `${sqft} sqft` : '--'} </Text>
      </View>
      <View style={styles.amenitiesColumn}>
        <Text style={styles.body}>{laundry}</Text>
        <Text style={styles.body}>{parking}</Text>
        <Text style={styles.body}>{heating}</Text>
      </View>
      <View style={styles.petsColumn}>
        <Text style={styles.body}>{pets}</Text>
      </View>
    </View>
  );
};

interface CompsTableProps {
  comparables: Array<Comparables>;
}

const CompsTable: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<CompsTableProps>>> = (
  props,
) => {
  const { comparables } = props;
  return (
    <View style={styles.flexColumn}>
      <View style={styles.tableHeader}>
        <View style={styles.imageColumn}></View>
        <View style={styles.priceColumn}>
          <Text style={styles.bodyHeading}>Price</Text>
        </View>
        <View style={styles.locationColumn}>
          <Text style={styles.bodyHeading}>Location</Text>
        </View>
        <View style={styles.bedsColumn}>
          <Text style={styles.bodyHeading}>Beds</Text>
        </View>
        <View style={styles.bathsColumn}>
          <Text style={styles.bodyHeading}>Baths</Text>
        </View>
        <View style={styles.sizeColumn}>
          <Text style={styles.bodyHeading}>Size</Text>
        </View>
        <View style={styles.amenitiesColumn}>
          <Text style={styles.bodyHeading}>Amenities</Text>
        </View>
        <View style={styles.petsColumn}></View>
      </View>
      {comparables
        .filter((comp) => !comp.isUsersComp)
        .map((comp, i) => {
          return <Row isStriped={i % 2 === 0} comp={comp} key={`listing-${i}`} />;
        })}
    </View>
  );
};

export default CompsTable;
